"use client"

import { TrendingUp, TrendingDown, Minus } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import type { MarketRow } from "@/hooks/use-agent-dashboard"

interface MarketOverviewProps {
  selectedPair: string | null
  onSelectPair: (pair: string) => void
  rows?: MarketRow[]
}

/** Keeps the quotes table compact on desktop; scroll when the universe is long. */
const QUOTES_SCROLL_CLASS = "h-[min(420px,50vh)]"

export function MarketOverview({ selectedPair, onSelectPair, rows }: MarketOverviewProps) {
  const dataRows = rows ?? []
  return (
    <Card className="min-w-0 w-full bg-card border-border self-start">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-lg font-semibold">Market Overview</CardTitle>
        <Badge variant="outline" className="text-xs">
          MT5 quotes
        </Badge>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-[1fr,80px,80px,70px] gap-2 text-xs text-muted-foreground uppercase tracking-wider pb-2 border-b border-border">
          <div>Pair</div>
          <div className="text-right">Bid</div>
          <div className="text-right">Ask</div>
          <div className="text-right">D1 %</div>
        </div>
        <ScrollArea className={QUOTES_SCROLL_CLASS}>
          <div className="space-y-1 pr-3 pt-1">
            {dataRows.length === 0 && (
              <div className="py-10 text-center text-sm text-muted-foreground">
                No MetaTrader quotes yet. Start the backend with MT5 connected and refresh the dashboard.
              </div>
            )}
            {dataRows.map((data) => (
              <button
                key={data.pair}
                onClick={() => onSelectPair(data.pair)}
                className={`w-full grid grid-cols-[1fr,80px,80px,70px] gap-2 items-center py-2 rounded-md px-2 transition-all text-left ${
                  selectedPair === data.pair
                    ? "bg-primary/15 border border-primary/30"
                    : "hover:bg-secondary/50 border border-transparent"
                }`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {data.trend === "up" ? (
                    <TrendingUp className="h-3.5 w-3.5 shrink-0 text-positive" />
                  ) : data.trend === "down" ? (
                    <TrendingDown className="h-3.5 w-3.5 shrink-0 text-negative" />
                  ) : (
                    <Minus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  )}
                  <span className="font-mono font-medium text-sm truncate">{data.pair}</span>
                </div>
                <div className="text-right font-mono text-sm tabular-nums">{data.bid}</div>
                <div className="text-right font-mono text-sm tabular-nums">{data.ask}</div>
                <div
                  className={`text-right font-mono text-sm font-medium tabular-nums ${
                    data.trend === "up"
                      ? "text-positive"
                      : data.trend === "down"
                        ? "text-negative"
                        : "text-muted-foreground"
                  }`}
                >
                  {data.change}
                </div>
              </button>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  )
}
